import React, { useState, useEffect } from 'react';
// portalsafe-7ca145c4 — requests come from the getPortalRequests callable (subject, description,
// status, createdAt only — functions/utils/portalRequests.js). This page used to read whole request
// documents from Firestore, which carried the assigned staff member's id and name.
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, MessageSquare } from 'lucide-react';

const PortalRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRequests = async () => {
            const user = auth.currentUser;
            if (!user) return;

            try {
                const tokenResult = await user.getIdTokenResult();
                const orgId = tokenResult.claims.organizationId; // customer + company are checked on the server

                if (!orgId) {
                    setLoading(false);
                    return;
                }

                const res = await httpsCallable(functions, 'getPortalRequests')(); // portalsafe-7ca145c4
                const fetchedRequests = Array.isArray(res.data?.requests) ? res.data.requests : [];

                setRequests(fetchedRequests);
            } catch (error) {
                console.error("Error fetching requests:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-green-600" /></div>;

    return (
        <div className="max-w-4xl">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Work Requests</h1>
                    <p className="text-gray-500 mt-1">Check status of your requests</p>
                </div>
                <button 
                    onClick={() => navigate('/portal/requests/new')}
                    className="hidden md:flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-semibold transition-colors shadow-sm"
                >
                    <Plus size={18} /> New Request
                </button>
            </div>

            {requests.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="text-green-600" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">Need some work done?</h3>
                    <p className="text-gray-500 mt-1 mb-6">Send us a request and we'll fill in the details.</p>
                    <button 
                        onClick={() => navigate('/portal/requests/new')}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-md font-bold transition-colors shadow-sm"
                    >
                        Create New Request
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {requests.map((req, idx) => (
                        <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-bold text-gray-900">{req.subject || "General Request"}</h3>
                                    <p className="text-gray-600 mt-1">{req.description}</p>
                                    <p className="text-xs text-gray-400 mt-3">
                                        Sent on {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : 'Unknown Date'}
                                    </p>
                                </div>
                                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full uppercase">
                                    {req.status || 'New'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PortalRequests;